import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import usePageMetadata from '../utils/usePageMetadata';
import './MetricsDictionary.css';

const MetricsDictionary = () => {
  const location = useLocation();
  
  usePageMetadata({
    title: 'Analytics Metrics Dictionary - The Ad Shop',
    description: 'Complete guide to Google Analytics 4 (GA4), AdSense, and Search Console metrics. Learn what each metric means, how it\'s calculated, and how to use it.',
    keywords: 'GA4 metrics, Google Analytics metrics, AdSense metrics, Search Console metrics, analytics dictionary, metric definitions'
  });

  // Scroll to anchor on mount if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  const ga4Metrics = [
    {
      id: 'users',
      name: 'Total Users',
      description: 'The total number of unique visitors to your website over the selected time period. Each person is counted only once, regardless of how many times they visit.',
      formula: 'Count of unique user IDs',
      category: 'User Metrics'
    },
    {
      id: 'newUsers',
      name: 'New Users',
      description: 'The number of users who visited your site for the first time during the selected time period.',
      formula: 'Count of unique user IDs where first_visit = true',
      category: 'User Metrics'
    },
    {
      id: 'returnUsers',
      name: 'Returning Users',
      description: 'The number of users who have visited your site before and returned during the selected time period. This is fetched directly from GA4 using the newVsReturning dimension.',
      formula: 'Count of activeUsers where newVsReturning dimension = "returning"',
      category: 'User Metrics'
    },
    {
      id: 'returningUsersPercentage',
      name: '% Returning',
      description: 'The percentage of total users who are returning visitors.',
      formula: '(Returning Users / Total Users) × 100',
      category: 'User Metrics'
    },
    {
      id: 'sessions',
      name: 'Sessions',
      description: 'Individual visits to your website. A session starts when a user arrives and ends after 30 minutes of inactivity, at midnight, or when a new campaign source is detected.',
      formula: 'Count of session_start events',
      category: 'Session Metrics'
    },
    {
      id: 'pageviews',
      name: 'Pageviews',
      description: 'The total number of pages viewed by users. Repeated views of a single page are counted.',
      formula: 'Count of page_view events',
      category: 'Session Metrics'
    },
    {
      id: 'adImpressions',
      name: 'Ad Impressions',
      description: 'The total number of times ads were displayed on your pages.',
      formula: 'Count of ad_impression events',
      category: 'Session Metrics'
    },
    {
      id: 'engagedSessions',
      name: 'Engaged Sessions',
      description: 'Sessions that lasted longer than 10 seconds, had 2 or more pageviews, or included a conversion event.',
      formula: 'Sessions where duration > 10s OR pageviews ≥ 2 OR conversion occurred',
      category: 'Engagement Metrics'
    },
    {
      id: 'engagedSessionsPerUser',
      name: 'Engaged Sessions/User',
      description: 'Average number of engaged sessions per returning user. This metric is calculated specifically for returning users to measure their engagement level.',
      formula: 'Total Engaged Sessions (for returning users) / Returning Users',
      category: 'Engagement Metrics'
    },
    {
      id: 'engagementRate',
      name: 'Engagement Rate',
      description: 'The percentage of sessions that were engaged. A session is engaged if it lasts longer than 10 seconds, has 2+ pageviews, or includes a conversion.',
      formula: '(Engaged Sessions / Total Sessions) × 100',
      category: 'Engagement Metrics'
    },
    {
      id: 'engagementTime',
      name: 'Avg Engagement Time',
      description: 'Average time users spent actively engaged with your site. For returning users, this shows the average engagement time specifically for that segment.',
      formula: 'Sum of engagement_time_msec / Engaged Sessions (for returning users)',
      category: 'Engagement Metrics'
    },
    {
      id: 'bounceRate',
      name: 'Bounce Rate',
      description: 'The percentage of single-page sessions where the user left without interacting with the page.',
      formula: '(Single-page sessions / Total Sessions) × 100',
      category: 'Engagement Metrics'
    },
    {
      id: 'usersPerSession',
      name: 'Users/Session',
      description: 'Average number of users per session. Values greater than 1 indicate multiple users may be sharing devices or sessions.',
      formula: 'Total Users / Total Sessions',
      category: 'Engagement Metrics'
    },
    {
      id: 'pageviewsPerUser',
      name: 'Views/User',
      description: 'Average number of pageviews per user, indicating how deeply users explore your site.',
      formula: 'Total Pageviews / Total Users',
      category: 'Engagement Metrics'
    }
  ];

  const adsenseMetrics = [
    {
      id: 'revenue',
      name: 'Total Revenue',
      description: 'Total earnings from AdSense ads displayed on your website during the selected time period.',
      formula: 'Sum of all ad revenue',
      category: 'Revenue Metrics'
    },
    {
      id: 'rpm',
      name: 'RPM (Revenue Per Mille)',
      description: 'Estimated revenue per 1,000 pageviews. RPM helps you understand how much revenue you earn per thousand pageviews.',
      formula: '(Total Revenue / Total Pageviews) × 1,000',
      category: 'Revenue Metrics'
    },
    {
      id: 'cpm',
      name: 'CPM (Cost Per Mille)',
      description: 'Average cost per 1,000 ad impressions. This represents what advertisers pay per thousand impressions.',
      formula: '(Total Ad Cost / Total Ad Impressions) × 1,000',
      category: 'Revenue Metrics'
    },
    {
      id: 'rpu',
      name: 'RPU (Revenue Per User)',
      description: 'Average revenue generated per user. This metric helps you understand the monetization value of each visitor.',
      formula: 'Total Revenue / Total Users',
      category: 'Revenue Metrics'
    }
  ];

  const searchConsoleMetrics = [
    {
      id: 'impressions',
      name: 'Impressions',
      description: 'How many times your pages appeared in Google search results for users.',
      formula: 'Count of times URLs appeared in search results',
      category: 'Search Performance'
    },
    {
      id: 'clicks',
      name: 'Clicks',
      description: 'How many times users clicked on your pages in Google search results.',
      formula: 'Count of clicks from search results',
      category: 'Search Performance'
    },
    {
      id: 'ctr',
      name: 'CTR (Click-Through Rate)',
      description: 'The percentage of impressions that resulted in clicks.',
      formula: '(Clicks / Impressions) × 100',
      category: 'Search Performance'
    },
    {
      id: 'position',
      name: 'Average Position',
      description: 'The average position of your pages in Google search results.',
      formula: 'Sum of positions / Number of impressions',
      category: 'Search Performance'
    }
  ];

  const MetricSection = ({ title, metrics, source }) => (
    <section className="metric-section" id={source.toLowerCase()}>
      <h2 className="section-title">{title}</h2>
      <div className="metrics-grid">
        {metrics.map((metric) => (
          <div key={metric.id} id={metric.id} className="metric-card">
            <div className="metric-header">
              <h3 className="metric-name">{metric.name}</h3>
              <span className="metric-category">{metric.category}</span>
            </div>
            <div className="metric-content">
              <p className="metric-description">{metric.description}</p>
              <div className="metric-formula">
                <strong>Formula:</strong> <code>{metric.formula}</code>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );

  return (
    <div className="metrics-dictionary">
      <div className="dictionary-container">
        <header className="dictionary-header">
          <h1>Analytics Metrics Dictionary</h1>
          <p className="dictionary-intro">
            Complete guide to understanding all metrics in The Ad Shop dashboard. 
            Metrics are organized by data source: Google Analytics 4 (GA4), AdSense, and Search Console.
          </p>
        </header>

        <nav className="dictionary-nav">
          <a href="#ga4" className="nav-link">Google Analytics 4</a>
          <a href="#adsense" className="nav-link">AdSense</a>
          <a href="#searchconsole" className="nav-link">Search Console</a>
        </nav>

        <MetricSection 
          title="Google Analytics 4 (GA4) Metrics" 
          metrics={ga4Metrics} 
          source="ga4"
        />

        <MetricSection 
          title="AdSense Metrics" 
          metrics={adsenseMetrics} 
          source="adsense"
        />

        <MetricSection 
          title="Search Console Metrics" 
          metrics={searchConsoleMetrics} 
          source="searchconsole"
        />
      </div>
    </div>
  );
};

export default MetricsDictionary;

